import classNames from "classnames";
import { DragIcon } from "components/icons/DragIcon";
import { ResizableIcon } from "components/icons/ResizableIcon";
import { useCallback, useEffect, useRef, useState } from "react";

type Direction = "left" | "right" | "top" | "bottom";

const COLLAPSE_DELAY_MS = 400;

const clearTimer = (
  timer: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
) => {
  if (!timer.current) return;
  clearTimeout(timer.current);
  timer.current = null;
};

type Props = {
  children: React.ReactNode;
  defaultSize: number;
  minSize?: number;
  maxSize?: number;
  handleDirection?: Direction;
  resetOnOpen?: boolean;
  className?: string;
  onResize?: (size: number) => void;
  onCollapse?: () => void;
};

export const ResizablePanel = ({
  children,
  defaultSize,
  minSize = 200,
  maxSize = 800,
  handleDirection = "left",
  resetOnOpen = false,
  className,
  onResize,
  onCollapse,
}: Props) => {
  const [size, setSize] = useState(defaultSize);
  const prevResetOnOpen = useRef(resetOnOpen);

  useEffect(() => {
    if (resetOnOpen && !prevResetOnOpen.current) {
      setSize(defaultSize);
    }
    prevResetOnOpen.current = resetOnOpen;
  }, [resetOnOpen, defaultSize]);
  const isDragging = useRef(false);
  const startPos = useRef(0);
  const startSize = useRef(defaultSize);
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => clearTimer(collapseTimer), []);

  const isHorizontal =
    handleDirection === "left" || handleDirection === "right";

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      isDragging.current = true;
      startPos.current = isHorizontal ? e.clientX : e.clientY;
      startSize.current = size;
      e.preventDefault();
    },
    [size, isHorizontal]
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;

      const current = isHorizontal ? e.clientX : e.clientY;
      const delta =
        handleDirection === "left" || handleDirection === "top"
          ? startPos.current - current
          : current - startPos.current;
      const draggedSize = startSize.current + delta;
      const isBelowMin = draggedSize < minSize;

      if (onCollapse && isBelowMin && !collapseTimer.current) {
        collapseTimer.current = setTimeout(() => {
          isDragging.current = false;
          collapseTimer.current = null;
          onCollapse();
        }, COLLAPSE_DELAY_MS);
      }

      if (!isBelowMin) clearTimer(collapseTimer);

      const newSize = Math.min(maxSize, Math.max(minSize, draggedSize));
      setSize(newSize);
      onResize?.(newSize);
    };

    const onMouseUp = () => {
      isDragging.current = false;
      clearTimer(collapseTimer);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [handleDirection, isHorizontal, minSize, maxSize, onResize, onCollapse]);

  const handleEdgeClass = {
    left: "t-inset-y-0 t-left-0 t-w-px t-cursor-col-resize t-flex-col -t-translate-x-1/2",
    right:
      "t-inset-y-0 t-right-0 t-w-px t-cursor-col-resize t-flex-col t-translate-x-1/2",
    top: "t-inset-x-0 t-top-0 t-h-px t-cursor-row-resize t-flex-row -t-translate-y-1/2",
    bottom:
      "t-inset-x-0 t-bottom-0 t-h-px t-cursor-row-resize t-flex-row t-translate-y-1/2",
  }[handleDirection];

  return (
    <div
      className={classNames(
        "t-relative t-shrink-0 t-overflow-visible",
        className
      )}
      style={{ [isHorizontal ? "width" : "height"]: size }}
    >
      <div className="t-w-full t-h-full t-overflow-auto">{children}</div>

      <div
        onMouseDown={onMouseDown}
        className={classNames(
          "t-group t-absolute t-select-none t-flex t-items-center t-justify-center t-border-neutral-10 hover:t-border-purple-50 t-z-10",
          "t-border-solid",
          {
            "t-border-l": handleDirection === "left",
            "t-border-r": handleDirection === "right",
            "t-border-t": handleDirection === "top",
            "t-border-b": handleDirection === "bottom",
          },
          handleEdgeClass
        )}
      >
        <div className="t-bg-white t-rounded-full t-border t-border-solid t-border-neutral-10 t-shadow-light-30 t-z-dropdown t-flex t-items-center t-justify-end t-w-8 t-h-8 t-pr-1.5 t-overflow-hidden">
          <ResizableIcon className="t-rotate-90 t-shrink-0 t-text-neutral-40" />
        </div>
      </div>
    </div>
  );
};
